import { use, useMemo, useRef } from "react";
import { Form, Modal, Table, Button, Container, Card } from "react-bootstrap";
import Select from "react-select";
import * as XLSX from "xlsx";
import { ExcelIcon } from "../../Icons";

function BudgetDetailsModal({
  showBudgetModal,
  setBudgetModal,
  setEditingBudget,
  editingBudget,
  setupData,
  budgetFormik,
  accountsLoading,
  accounts,
  renderNestedAccounts,
  createBudgetMutation,
  updateBudgetMutation,
  setShowSetupModal,
}) {
  const tableRef = useRef(null);

  const exportToExcel = () => {
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: "Sheet1" });

    // Create a safe filename for all OSes by replacing invalid characters with underscores
    const rawFilename = `${editingBudget.budgetType}_${
      editingBudget.period
    }_${new Date().toISOString().slice(0, 10)}`;
    const filename = rawFilename.replace(/[^a-zA-Z0-9-_]/g, "_");

    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const year = useMemo(() => {
    return setupData?.period?.split(" ")[1] || "2025";
  }, [setupData]);

  return (
    <Modal
      show={showBudgetModal}
      onHide={() => {
        setBudgetModal(false);
        setEditingBudget(null);
      }}
      size="xl"
      fullscreen
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {editingBudget ? "Edit Budget Details" : "Budget Details"} -{" "}
          {setupData?.budgetType?.replace("_", " ")?.toUpperCase()}
          {editingBudget && (
            <Button variant="light-blue" size="sm" onClick={exportToExcel} className="ms-3">
              <ExcelIcon /> Export
            </Button>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body scrollable>
        <Form onSubmit={budgetFormik.handleSubmit} id="CREATE_BUDGET_FORM">
          <div
            className="table-responsive"
            style={{ height: "calc(100vh - 11rem)", overflowY: "auto" }}
          >
            <Table
              striped
              bordered
              hover
              size="sm"
              className="sticky-first-col border"
              ref={tableRef}
            >
              <thead className="text-center text-nowrap">
                <tr>
                  <th scope="col">Accounts</th>
                  <th scope="col">Actuals 2025 (YTD)</th>
                  <th scope="col">Budget totals</th>
                  <th scope="col">Jan {year}</th>
                  <th scope="col">Feb {year}</th>
                  <th scope="col">Mar {year}</th>
                  <th scope="col">Apr {year}</th>
                  <th scope="col">May {year}</th>
                  <th scope="col">Jun {year}</th>
                  <th scope="col">Jul {year}</th>
                  <th scope="col">Aug {year}</th>
                  <th scope="col">Sep {year}</th>
                  <th scope="col">Oct {year}</th>
                  <th scope="col">Nov {year}</th>
                  <th scope="col">Dec {year}</th>
                </tr>
              </thead>
              <tbody>
                {accountsLoading ? (
                  <tr>
                    <td colSpan="15" className="text-center">
                      Loading accounts...
                    </td>
                  </tr>
                ) : (
                  renderNestedAccounts(accounts)
                )}
              </tbody>
            </Table>
          </div>

          {budgetFormik.touched.accounts && budgetFormik.errors.accounts && (
            <div className="text-danger mb-3">{budgetFormik.errors.accounts}</div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer className="shadow">
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button
            variant="secondary"
            onClick={() => {
              setBudgetModal(false);
              setShowSetupModal(true);
            }}
          >
            Back
          </Button>
          <Button
            variant="primary"
            disabled={createBudgetMutation.isLoading || updateBudgetMutation.isLoading}
            onClick={() => budgetFormik.handleSubmit()}
          >
            {createBudgetMutation.isLoading || updateBudgetMutation.isLoading
              ? editingBudget
                ? "Updating..."
                : "Creating..."
              : editingBudget
              ? "Update Budget"
              : "Create Budget"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function BudgetSetupModal({
  setupFormik,
  showSetupModal,
  setShowSetupModal,
  editingBudget,
  periodOptions,
  profitLossOptions,
}) {
  return (
    <Modal
      show={showSetupModal}
      onHide={() => setShowSetupModal(false)}
      fullscreen
      animation={false}
    >
      <Modal.Header closeButton className="bg-lighter">
        <Modal.Title className="text-center">
          {editingBudget ? "Edit Budget Setup" : "Create New Budget"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body scrollable>
        <Form onSubmit={setupFormik.handleSubmit} id="BUDGET_FORM_SETUP">
          <Container>
            <h4 className="my-4"> How do you want to set up your budget?</h4>
            <Form.Group className="mb-3">
              <Form.Label>Budget type</Form.Label>
              <div className="d-flex gap-4">
                <Form.Check
                  type="radio"
                  label="Profit and loss"
                  name="budgetType"
                  value="profit_and_loss"
                  checked={setupFormik.values.budgetType === "profit_and_loss"}
                  onChange={setupFormik.handleChange}
                />
                <Form.Check
                  type="radio"
                  label="Balance sheet"
                  name="budgetType"
                  value="balance_sheet"
                  checked={setupFormik.values.budgetType === "balance_sheet"}
                  onChange={setupFormik.handleChange}
                />
              </div>
              {setupFormik.touched.budgetType && setupFormik.errors.budgetType && (
                <div className="text-danger">{setupFormik.errors.budgetType}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-3 col-md-3">
              <Form.Label>Period</Form.Label>
              <Select
                options={periodOptions}
                value={periodOptions.find(option => option.value === setupFormik.values.period)}
                onChange={selectedOption =>
                  setupFormik.setFieldValue("period", selectedOption.value)
                }
                onBlur={() => setupFormik.setFieldTouched("period", true)}
                placeholder="Select period..."
                isClearable={false}
                menuPosition="fixed"
              />
              {setupFormik.touched.period && setupFormik.errors.period && (
                <div className="text-danger">{setupFormik.errors.period}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Budget format</Form.Label>
              <div className="d-flex gap-4">
                <Form.Check
                  type="radio"
                  label="Consolidated"
                  name="budgetFormat"
                  value="consolidated"
                  checked={setupFormik.values.budgetFormat === "consolidated"}
                  onChange={setupFormik.handleChange}
                />
                <Form.Check
                  type="radio"
                  label="Subdivided"
                  name="budgetFormat"
                  value="subdivided"
                  checked={setupFormik.values.budgetFormat === "subdivided"}
                  onChange={setupFormik.handleChange}
                />
              </div>
              {setupFormik.touched.budgetFormat && setupFormik.errors.budgetFormat && (
                <div className="text-danger">{setupFormik.errors.budgetFormat}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-3 col-md-3">
              <Form.Label>Profit data</Form.Label>
              <Select
                options={profitLossOptions}
                value={profitLossOptions.find(
                  option => option.value === setupFormik.values.profitLoss
                )}
                onChange={selectedOption =>
                  setupFormik.setFieldValue("profitLoss", selectedOption?.value || "")
                }
                onBlur={() => setupFormik.setFieldTouched("profitLoss", true)}
                placeholder="Optional Select..."
                isClearable={true}
                menuPosition="fixed"
              />
            </Form.Group>

            <Form.Group className="mb-4 col-md-4">
              <Form.Label>Available setup option</Form.Label>
              <Card className="border-primary">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="me-3" style={{ fontSize: "2rem" }}>
                      📊
                    </div>
                    <div>
                      <strong>Custom budget</strong>
                      <p className="mb-0 text-muted">Create a budget from scratch</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Form.Group>

            <Button variant="link" type="button">
              Import budget
            </Button>
          </Container>
        </Form>
      </Modal.Body>
      <Modal.Footer className="shadow">
        <div className="d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={() => setShowSetupModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" form="BUDGET_FORM_SETUP" type="submit">
            Next
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export { BudgetDetailsModal, BudgetSetupModal };
